import { isMixedContentBlocked } from './probe';
import type { ProbeOutcome } from './types';

/**
 * How a row reads: **three states, not Android's two**.
 *
 * A mixed-content row is neither alive nor dead. Safari refuses a plain
 * `http://` request before anything leaves the phone (SPEC §7.3, §14 #3), so
 * that row is a property of the browser, not of the link — and a red dot in a
 * list is a verdict. `unmeasurable` is the honest third answer, and it is what
 * `ios_status_unmeasurable` says in words on the same row's tile.
 *
 * A classification, not a colour. The components map a tone onto a class name
 * (`c-alive` / `c-dead` / `c-warn`), and that mapping is the only part that is
 * about presentation. Keeping the classification here is what stops it drifting:
 * this rule was written out twice, in the dashboard's compact row and in the
 * results list's tile, each with a comment asking the next person to keep the
 * copies identical — because the same link showing amber in one list and red in
 * the other reads as a bug in the data rather than as two views of one row.
 */
export type ResultTone = 'alive' | 'dead' | 'unmeasurable';

/**
 * Takes any probe outcome, so a `CheckResult` fits without unwrapping.
 *
 * Note the order: the mixed-content test comes **first**, because such a row is
 * `unhealthy` in the submitted data (the filter and the payload agree on that,
 * see `resultFilter.ts`) and testing `healthStatus` first would silently call it
 * dead.
 */
export function resultTone(result: ProbeOutcome): ResultTone {
  if (isMixedContentBlocked(result)) return 'unmeasurable';
  return result.healthStatus === 'healthy' ? 'alive' : 'dead';
}
